import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { ACL_TYPE } from '@openx/types/aclType';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import type React from 'react';
import { useCallback } from 'react';
import { type FieldArrayWithId, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIsAllowed } from '../../../../../../../configs/permission/utils/useIsAllowed';
import { exportType, MatchingOperator } from '../../../../constants';
import {
  AudienceFields,
  type AudienceForm,
  type AudienceListType,
  type Group,
  type GroupFieldName,
  type GroupsPath,
} from '../../../../types';
import { extractNonEmptyGroup } from '../../../../utils/extractNonEmptyGroup';
import { isGroupReallyEmpty } from '../../../../utils/isGroupReallyEmpty';
import type { MarketOperatorRestrictionReason } from '../../../../utils/marketOperatorRestrictions';
import { OperatorChip, OperatorType } from '../../../DimensionChip/OperatorChip';
import type { OperatorPath } from '../../../StructureAndLogic/components/OperatorSelector';
import { useDealsRevenueMethod } from '../../../StructureAndLogic/components/useDealsRevenueMethod';
import type { SelectedLocationSegment } from '../../constants';
import type { DrawerState } from '../../drawerTypes';
import { isGroup } from '../utils/guards';
import { GroupContentLegacy } from './GroupContentLegacy';
import { GroupContentRegionFirst } from './GroupContentRegionFirst';
import type { GroupContentBaseProps } from './groupContentTypes';

export type SubGroupItemProps = {
  field: FieldArrayWithId<AudienceForm, GroupsPath, 'id'>;
  index: number;
  fieldName: `${GroupsPath}.${number}`;
  parentIndex: number;
  isLastItem: boolean;
  listType: AudienceListType;
  setDrawerState: React.Dispatch<React.SetStateAction<DrawerState>>;
  typedUpdate: (index: number, value: Group) => void;
  onRemove: (index: number, isEmpty: boolean, field: Group) => void;
  handleToggleOperator: () => void;
  isEdit: boolean;
  shouldUseRegionFirstFlow: boolean;
  siblingOperatorRestrictionReason: MarketOperatorRestrictionReason | null;
  selectedLocationSegment: SelectedLocationSegment;
  handleSelectedLocationSegmentChange: (segment: SelectedLocationSegment, index: number | string) => void;
  isExpanded: (id?: string) => boolean;
  toggle: (id?: string) => () => void;
  setExpanded: (id: string | undefined, expanded: boolean) => void;
};

export const SubGroupItem: React.FC<SubGroupItemProps> = ({
  field,
  index,
  fieldName,
  parentIndex,
  isLastItem,
  listType,
  isEdit,
  shouldUseRegionFirstFlow,
  siblingOperatorRestrictionReason,
  setDrawerState,
  typedUpdate,
  onRemove,
  handleToggleOperator,
  selectedLocationSegment,
  isExpanded,
  toggle,
  setExpanded,
  handleSelectedLocationSegmentChange,
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext<AudienceForm>();
  const { isAllowed } = useIsAllowed();
  const { havePoM: disabledDueToDealRevenueMethod, isLoading } = useDealsRevenueMethod();

  const structurallyEmpty = !extractNonEmptyGroup(field);
  const functionallyEmpty = isGroupReallyEmpty(field);
  const isEmptyGroup = structurallyEmpty || functionallyEmpty;

  const subgroupName = t('Subgroup {parentCount}.{childCount}', {
    childCount: index + 1,
    parentCount: parentIndex + 1,
  });

  const groupFieldName: GroupFieldName = `${fieldName}.groups.${index}`;
  const operatorPath: OperatorPath = `${fieldName}.operator`;

  const observableGroupOperator = useWatch({
    name: operatorPath,
    control,
  });

  const observableMatchType = useWatch({
    name: AudienceFields.ExportType,
    control,
  });

  const isIdentityGraphMatchType = observableMatchType === exportType.OA_MATCH;
  const isAllowedToSeeLookalikeSection = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.LOOKALIKE_SEGMENTS_ENABLED));

  const currentGroup = isGroup(field) ? field : undefined;
  const hasLookalikeSegmentsOnEdit = !!(isEdit && currentGroup?.lookalike?.segments?.length);
  const shouldDisplayLookalikeSection = isAllowedToSeeLookalikeSection || hasLookalikeSegmentsOnEdit;

  const selectedRegionCode = currentGroup?.region ?? null;
  const subgroupToggleId = field.uuid ?? field.id;
  const fieldWithToggleId: GroupContentBaseProps['field'] | null = currentGroup
    ? {
        ...field,
        uuid: subgroupToggleId,
      }
    : null;
  const subgroupOperatorChipReadonly =
    !isEdit || (shouldUseRegionFirstFlow && siblingOperatorRestrictionReason != null);

  const groupContentBaseProps: GroupContentBaseProps | null = fieldWithToggleId
    ? {
        field: fieldWithToggleId,
        groupFieldName,
        groupName: subgroupName,
        index,
        isEdit,
        isEmptyGroup,
        listType,
        setDrawerState,
        update: typedUpdate,
        isExpanded,
        toggle,
        setExpanded,
        selectedLocationSegment,
        handleSelectedLocationSegmentChange,
        isIdentityGraphMatchType,
        shouldDisplayLookalikeSection: shouldDisplayLookalikeSection ?? false,
      }
    : null;

  const handleRemoveClick = useCallback(() => {
    onRemove(index, isEmptyGroup, field);
  }, [onRemove, index, isEmptyGroup, field]);

  return (
    <>
      <Stack
        direction="row"
        className="subgroup-title-container"
        data-test={`subgroup-section-${subgroupName.toLowerCase()}`}
        sx={{
          p: 1,
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Typography data-test="subgroup-title" variant="h3">
          {subgroupName}
        </Typography>

        <Stack
          direction="row"
          sx={{
            gap: 1,
          }}
        >
          {isEdit && (
            <Button
              color="error"
              data-test="remove-subgroup-button"
              onClick={handleRemoveClick}
              variant="text"
              component="span"
            >
              {t('Remove Sub-Group')}
            </Button>
          )}

          <ListItemButton onClick={toggle(subgroupToggleId)} sx={{ padding: 0 }}>
            <ListItemIcon sx={{ minWidth: 'unset' }}>
              {isExpanded(subgroupToggleId) ? (
                <ExpandLess data-test="expand-less-icon" />
              ) : (
                <ExpandMore data-test="expand-more-icon" />
              )}
            </ListItemIcon>
          </ListItemButton>
        </Stack>
      </Stack>
      {isExpanded(subgroupToggleId) && groupContentBaseProps && (
        <Box data-test={`subgroup-content-${subgroupName.toLowerCase()}`}>
          {shouldUseRegionFirstFlow ? (
            <GroupContentRegionFirst {...groupContentBaseProps} selectedRegionCode={selectedRegionCode} />
          ) : (
            <GroupContentLegacy {...groupContentBaseProps} />
          )}
        </Box>
      )}

      {!isLastItem && (
        <OperatorChip
          operator={observableGroupOperator === MatchingOperator.INTERSECTION ? OperatorType.AND : OperatorType.OR}
          {...(subgroupOperatorChipReadonly ? { readonly: true as const } : { onOperatorChange: handleToggleOperator })}
          dataTest={'subgroup-operator-chip'}
          disabled={isEdit && disabledDueToDealRevenueMethod}
          loading={isLoading}
        />
      )}
    </>
  );
};
